import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { LayoutDashboard, Users, CalendarDays, CreditCard, Plus, Loader2, GraduationCap } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { LessonCard } from "@/components/LessonCard";
import { BookingDialog } from "@/components/BookingDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, gbp, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const nav = [
  { label: "Overview", href: "#overview", icon: LayoutDashboard },
  { label: "My Children", href: "#children", icon: Users },
  { label: "Lessons", href: "#lessons", icon: CalendarDays },
  { label: "Payments", href: "#payments", icon: CreditCard },
];

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{label}</p>
        <Icon className="h-5 w-5 text-[#BA8A30]" />
      </div>
      <p className="font-display text-3xl font-bold text-[#595C2B] mt-2">{value}</p>
    </div>
  );
}

function AddChild({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", level: "GCSE", year_group: "", student_email: "" });
  const submit = async () => {
    if (!f.name) return toast.error("Enter a name");
    setLoading(true);
    try {
      await api.post("/children", { ...f, student_email: f.student_email || undefined });
      toast.success("Child added");
      setOpen(false); setF({ name: "", level: "GCSE", year_group: "", student_email: "" });
      onAdded();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-[#595C2B]/20" data-testid="add-child-btn"><Plus className="h-4 w-4 mr-2" />Add child</Button>
      </DialogTrigger>
      <DialogContent data-testid="add-child-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#595C2B]">Add a child</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="child-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Level</Label>
              <Select value={f.level} onValueChange={(v) => setF({ ...f, level: v })}>
                <SelectTrigger className="mt-1" data-testid="child-level-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="GCSE">GCSE</SelectItem><SelectItem value="A-Level">A-Level</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Year group</Label><Input value={f.year_group} onChange={(e) => setF({ ...f, year_group: e.target.value })} placeholder="Year 11" className="mt-1" data-testid="child-year-input" /></div>
          </div>
          <div><Label>Student email (optional)</Label><Input type="email" value={f.student_email} onChange={(e) => setF({ ...f, student_email: e.target.value })} placeholder="Links their student login" className="mt-1" data-testid="child-email-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="w-full rounded-full bg-[#595C2B] hover:bg-[#45481F] text-white" data-testid="save-child-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ParentDashboard() {
  const qc = useQueryClient();
  const { data: children = [] } = useQuery({ queryKey: ["children"], queryFn: async () => (await api.get("/children")).data });
  const { data: tutors = [] } = useQuery({ queryKey: ["tutors"], queryFn: async () => (await api.get("/tutors")).data });
  const { data: lessons = [], isLoading } = useQuery({ queryKey: ["lessons"], queryFn: async () => (await api.get("/lessons")).data });

  const upcoming = lessons.filter((l) => l.payment_status === "paid");
  const paidTotal = lessons.filter((l) => l.payment_status === "paid").reduce((s, l) => s + l.amount, 0);
  const reload = () => qc.invalidateQueries();

  return (
    <DashboardShell title="Parent Dashboard" subtitle="Book lessons, manage children and payments" nav={nav}>
      <div className="flex flex-wrap items-center gap-3 mb-8" id="overview">
        <BookingDialog children={children} tutors={tutors} onBooked={reload} />
        <AddChild onAdded={reload} />
      </div>

      <div className="grid sm:grid-cols-3 gap-6 mb-10">
        <Kpi label="Confirmed lessons" value={upcoming.length} icon={CalendarDays} />
        <Kpi label="Children" value={children.length} icon={Users} />
        <Kpi label="Total paid" value={gbp(paidTotal)} icon={CreditCard} />
      </div>

      <section id="children" className="mb-10">
        <h2 className="font-display text-2xl font-semibold text-[#595C2B] mb-4">My children</h2>
        {children.length === 0 ? (
          <p className="text-slate-500">No children added yet. Add one to start booking.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {children.map((c) => (
              <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4" data-testid={`child-${c.id}`}>
                <div className="h-11 w-11 rounded-xl bg-[#595C2B]/10 flex items-center justify-center"><GraduationCap className="h-5 w-5 text-[#595C2B]" /></div>
                <div>
                  <p className="font-semibold text-[#595C2B]">{c.name}</p>
                  <p className="text-sm text-slate-500">{c.level}{c.year_group ? ` · ${c.year_group}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section id="lessons" className="mb-10">
        <h2 className="font-display text-2xl font-semibold text-[#595C2B] mb-4">Upcoming & recent lessons</h2>
        {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-[#595C2B]" /> : lessons.length === 0 ? (
          <p className="text-slate-500">No lessons booked yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {lessons.map((l, i) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <LessonCard lesson={l} who="parent" />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section id="payments">
        <h2 className="font-display text-2xl font-semibold text-[#595C2B] mb-4">Payment history</h2>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Lesson</TableHead><TableHead>Child</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {lessons.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No payments yet</TableCell></TableRow>
              ) : lessons.map((l) => (
                <TableRow key={l.id} data-testid={`payment-row-${l.id}`}>
                  <TableCell className="font-medium">{l.subject} ({l.level})</TableCell>
                  <TableCell>{l.child_name}</TableCell>
                  <TableCell>{gbp(l.amount)}</TableCell>
                  <TableCell className="capitalize">{l.payment_status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </DashboardShell>
  );
}
